'use client'; // src/form-control.tsx

var _excluded = ["id", "isRequired", "isInvalid", "isDisabled", "isReadOnly"],
    _excluded2 = ["getRootProps", "htmlProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import { createContext } from "@chakra-ui/react-context";
import { mergeRefs } from "@chakra-ui/react-use-merge-refs";
import { chakra, forwardRef, omitThemingProps, useMultiStyleConfig } from "@chakra-ui/system";
import { cx, dataAttr } from "@chakra-ui/shared-utils";
import { useCallback, useId, useState } from "react";
import { jsx } from "react/jsx-runtime";

var _createContext = createContext({
  name: "FormControlStylesContext",
  errorMessage: "useFormControlStyles returned is 'undefined'. Seems you forgot to wrap the components in \"<FormControl />\" "
}),
    _createContext2 = _slicedToArray(_createContext, 2),
    FormControlStylesProvider = _createContext2[0],
    useFormControlStyles = _createContext2[1];

var _createContext3 = createContext({
  strict: false,
  name: "FormControlContext"
}),
    _createContext4 = _slicedToArray(_createContext3, 2),
    FormControlProvider = _createContext4[0],
    useFormControlContext = _createContext4[1];

function useFormControlProvider(props) {
  var idProp = props.id,
      isRequired = props.isRequired,
      isInvalid = props.isInvalid,
      isDisabled = props.isDisabled,
      isReadOnly = props.isReadOnly,
      htmlProps = _objectWithoutProperties(props, _excluded);

  var uuid = useId();
  var id = idProp || "field-".concat(uuid);
  var labelId = "".concat(id, "-label");
  var feedbackId = "".concat(id, "-feedback");
  var helpTextId = "".concat(id, "-helptext");

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasFeedbackText = _useState2[0],
      setHasFeedbackText = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      hasHelpText = _useState4[0],
      setHasHelpText = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isFocused = _useState6[0],
      setFocus = _useState6[1];

  var getHelpTextProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({
      id: helpTextId
    }, props2), {}, {
      /**
       * Notify the field context when the help text is rendered on screen,
       * so we can apply the correct `aria-describedby` to the field (e.g. input, textarea).
       */
      ref: mergeRefs(forwardedRef, function (node) {
        if (!node) return;
        setHasHelpText(true);
      })
    });
  }, [helpTextId]);
  var getLabelProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      ref: forwardedRef,
      "data-focus": dataAttr(isFocused),
      "data-disabled": dataAttr(isDisabled),
      "data-invalid": dataAttr(isInvalid),
      "data-readonly": dataAttr(isReadOnly),
      id: props2.id !== void 0 ? props2.id : labelId,
      htmlFor: props2.htmlFor !== void 0 ? props2.htmlFor : id
    });
  }, [id, isDisabled, isFocused, isInvalid, isReadOnly, labelId]);
  var getErrorMessageProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({
      id: feedbackId
    }, props2), {}, {
      /**
       * Notify the field context when the error message is rendered on screen,
       * so we can apply the correct `aria-describedby` to the field (e.g. input, textarea).
       */
      ref: mergeRefs(forwardedRef, function (node) {
        if (!node) return;
        setHasFeedbackText(true);
      }),
      "aria-live": "polite"
    });
  }, [feedbackId]);
  var getRootProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread(_objectSpread({}, props2), htmlProps), {}, {
      ref: forwardedRef,
      role: "group",
      "data-focus": dataAttr(isFocused),
      "data-disabled": dataAttr(isDisabled),
      "data-invalid": dataAttr(isInvalid),
      "data-readonly": dataAttr(isReadOnly)
    });
  }, [htmlProps, isDisabled, isFocused, isInvalid, isReadOnly]);
  var getRequiredIndicatorProps = useCallback(function () {
    var props2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props2), {}, {
      ref: forwardedRef,
      role: "presentation",
      "aria-hidden": true,
      children: props2.children || "*"
    });
  }, []);
  return {
    isRequired: !!isRequired,
    isInvalid: !!isInvalid,
    isReadOnly: !!isReadOnly,
    isDisabled: !!isDisabled,
    isFocused: !!isFocused,
    onFocus: function onFocus() {
      return setFocus(true);
    },
    onBlur: function onBlur() {
      return setFocus(false);
    },
    hasFeedbackText: hasFeedbackText,
    setHasFeedbackText: setHasFeedbackText,
    hasHelpText: hasHelpText,
    setHasHelpText: setHasHelpText,
    id: id,
    labelId: labelId,
    feedbackId: feedbackId,
    helpTextId: helpTextId,
    htmlProps: htmlProps,
    getHelpTextProps: getHelpTextProps,
    getErrorMessageProps: getErrorMessageProps,
    getRootProps: getRootProps,
    getLabelProps: getLabelProps,
    getRequiredIndicatorProps: getRequiredIndicatorProps
  };
}

var FormControl = forwardRef(function FormControl2(props, ref) {
  var styles = useMultiStyleConfig("Form", props);
  var ownProps = omitThemingProps(props);

  var _useFormControlProvid = useFormControlProvider(ownProps),
      getRootProps = _useFormControlProvid.getRootProps,
      _ = _useFormControlProvid.htmlProps,
      context = _objectWithoutProperties(_useFormControlProvid, _excluded2);

  var className = cx("chakra-form-control", props.className);
  return /* @__PURE__ */jsx(FormControlProvider, {
    value: context,
    children: /* @__PURE__ */jsx(FormControlStylesProvider, {
      value: styles,
      children: /* @__PURE__ */jsx(chakra.div, _objectSpread(_objectSpread({}, getRootProps({}, ref)), {}, {
        className: className,
        __css: styles["container"]
      }))
    })
  });
});
FormControl.displayName = "FormControl";
var FormHelperText = forwardRef(function FormHelperText2(props, ref) {
  var field = useFormControlContext();
  var styles = useFormControlStyles();
  var className = cx("chakra-form__helper-text", props.className);
  return /* @__PURE__ */jsx(chakra.div, _objectSpread(_objectSpread({}, field == null ? void 0 : field.getHelpTextProps(props, ref)), {}, {
    __css: styles.helperText,
    className: className
  }));
});
FormHelperText.displayName = "FormHelperText";
export { useFormControlStyles, useFormControlContext, FormControl, FormHelperText };